import React from 'react';
import { Link } from 'react-router-dom';
import styles from './AboutPage.module.css';

const principles = [
  {
    title: 'Simplicity',
    desc: 'LeanWeek removes noise from weekly fitness planning. Fewer decisions, better execution.',
  },
  {
    title: 'Privacy',
    desc: 'Your health and planning data should not become someone else’s business model.',
  },
  {
    title: 'Consistency',
    desc: 'The best fitness systems are the ones people can repeat week after week.',
  },
];

const storyCards = [
  {
    title: 'A calmer product philosophy',
    desc: 'LeanWeek was shaped as an antidote to loud, cluttered, addictive fitness products. We wanted a tool that feels deliberate, quiet, and useful.',
  },
  {
    title: 'Local-first by default',
    desc: 'Privacy is not a feature toggle. It is part of the architecture. LeanWeek is designed so your data stays close to you.',
  },
];

const AboutPage = () => {
  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroInner}>
            <span className={styles.eyebrow}>Why LeanWeek Exists</span>

            <h1 className={styles.title}>
              A sanctuary for <span>high-performance</span> minimalist health.
            </h1>

            <p className={styles.subtitle}>
              Beyond the noise of traditional fitness apps is a quieter,
              sharper, more intentional way to plan your week.
            </p>
          </div>
        </div>
      </header>

      <section className={styles.introSection}>
        <div className={styles.container}>
          <div className={styles.introGrid}>
            <div className={styles.introCopy}>
              <span className={styles.sectionKicker}>Built for real people</span>
              <h2>Not fitness extremes.</h2>
              <p>
                LeanWeek was built from a simple observation: most fitness
                software is optimized for obsession, not sustainable progress.
                Too much clutter. Too much pressure. Too little clarity.
              </p>
              <p>
                We wanted a weekly planning product that respects your attention,
                protects your privacy, and helps you stay consistent without
                turning wellness into performance theater.
              </p>
            </div>

            <div className={styles.visualPanel}>
              <div className={styles.visualGlow} />
              <div className={styles.visualFrame}>
                <div className={styles.visualRoom}>
                  <div className={styles.visualLamp} />
                  <div className={styles.visualLine} />
                  <div className={styles.visualDesk} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.problemSection}>
        <div className={styles.container}>
          <div className={styles.problemWrap}>
            <div className={styles.problemHeader}>
              <span className={styles.sectionKicker}>The Problem</span>
              <h2>The industry is loud. We are quiet.</h2>
            </div>

            <div className={styles.problemGrid}>
              <article className={styles.problemCard}>
                <h3>The friction of choice</h3>
                <p>
                  Most apps overwhelm users with thousands of options, endless
                  scrolling, and too many moving parts. LeanWeek narrows the
                  path and helps you act.
                </p>
              </article>

              <article className={styles.problemCard}>
                <h3>The data trap</h3>
                <p>
                  Too many products quietly convert personal health data into a
                  commercial asset. LeanWeek takes the opposite approach: your
                  data should remain yours.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.principlesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionKicker}>Core Principles</span>
            <h2>What guides the product</h2>
          </div>

          <div className={styles.principlesGrid}>
            {principles.map((item) => (
              <article key={item.title} className={styles.principleCard}>
                <div className={styles.principleIcon} />
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.storySection}>
        <div className={styles.container}>
          <div className={styles.storyGrid}>
            <div className={styles.storyCopy}>
              <span className={styles.sectionKicker}>The caselabs Story</span>
              <h2>Built by people who care about calm software.</h2>

              <p>
                LeanWeek is built by caselabs, a product studio focused on
                thoughtful digital tools that respect user attention and reduce
                noise instead of manufacturing it.
              </p>

              <p>
                We believe great fitness software should help people stay
                consistent, not anxious. It should give structure, not pressure.
                It should support discipline without becoming another source of
                digital chaos.
              </p>
            </div>

            <div className={styles.storyAside}>
              {storyCards.map((card) => (
                <article key={card.title} className={styles.storyCard}>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </article>
              ))}

              <div className={styles.storyVisual}>
                <div className={styles.storyVisualInner}>
                  <div className={styles.storyWindow} />
                  <div className={styles.storyDesk} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaBox}>
            <h2>Ready for a quieter approach?</h2>
            <p>
              Join the early access list and be among the first to experience
              LeanWeek.
            </p>

            <div className={styles.ctaActions}>
              <Link to="/waitlist" className={styles.primaryBtn}>
                Secure Your Spot
              </Link>
              <Link to="/features" className={styles.secondaryBtn}>
                View Features
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;